import React from 'react';
import { Link } from 'react-router-dom';
import logoImage from '../../assets/images/logo-community-impactinvest.png';
// Emblème seul (fond transparent) : lisible en petite taille dans l'en-tête
import emblemImage from '../../assets/images/logo-emblem.png';
import './LogoStyles.css';
import './StaticStyles.css';

/**
 * Composant Logo Community ImpactInvest - Optimisé pour une intégration excellente
 * Gère différentes tailles et variantes du logo avec un design moderne
 */
const CommunityLogo = ({ 
  size = 'md', 
  variant = 'default', 
  showText = true, 
  className = '',
  onClick = null,
  isLink = true 
}) => {
  // Configuration des tailles avec ratios optimisés (version imposante)
  const sizeConfig = {
    xs: { width: 70, height: 35, textSize: 'text-xs' },
    sm: { width: 110, height: 55, textSize: 'text-sm' },
    header: { width: 48, height: 48, textSize: 'text-lg font-bold', emblem: true },
    md: { width: 150, height: 75, textSize: 'text-base' },
    lg: { width: 190, height: 95, textSize: 'text-lg' },
    xl: { width: 230, height: 115, textSize: 'text-xl' },
    xxl: { width: 290, height: 145, textSize: 'text-2xl' }
  };

  // Configuration des variantes avec effets visuels stylisés (version statique)
  const variantConfig = {
    default: 'hover:scale-105 transition-all duration-300 rounded-xl p-2',
    minimal: 'hover:opacity-100 transition-all duration-200 rounded-lg p-1',
    prominent: 'hover:scale-105 transition-all duration-300 rounded-xl p-2',
    gradient: 'hover:brightness-110 transition-all duration-300 relative rounded-xl p-2',
    animated: 'hover:scale-105 transition-all duration-300 ease-in-out rounded-xl p-2'
  };

  const config = sizeConfig[size];
  const variantClass = variantConfig[variant];

  // Composant Logo avec image optimisée et style simple
  const LogoContent = () => (
    <div 
      className={`flex items-center space-x-3 ${variantClass} ${className} 
                  group relative cursor-pointer
                  focus:outline-none focus:ring-2 focus:ring-orange-500/50
                  active:scale-95 transition-all duration-200`}
      onClick={onClick}
      style={{ cursor: onClick || isLink ? 'pointer' : 'default' }}
      // Dans un lien, c'est le lien qui reçoit le focus (pas de bouton imbriqué)
      tabIndex={onClick && !isLink ? 0 : undefined}
      role={onClick && !isLink ? 'button' : undefined}
    >
      
      {/* Logo Image simple sans effets de défilement */}
      <div 
        className={`relative overflow-hidden rounded-lg transition-shadow duration-300 ${config.emblem ? '' : 'shadow-sm'}`}
        style={{ 
          width: config.width, 
          height: config.height,
          minWidth: config.width,
          minHeight: config.height
        }}
      >
        <img
          src={config.emblem ? emblemImage : logoImage}
          alt={showText ? '' : 'Community ImpactInvest - Pour une communauté résiliente'}
          width={config.width}
          height={config.height}
          className={`w-full h-full object-contain rounded-lg transition-all duration-300 ${
            config.emblem ? '' : 'bg-gradient-to-br from-orange-50 to-orange-100 p-2'
          }`}
          style={{
            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
          }}
          loading="eager"
          decoding="async"
        />
      </div>

      {/* Texte du logo simple sans effets de défilement */}
      {showText && (
        <div className="flex flex-col">
          <span className={`block font-bold leading-tight ${config.textSize} 
                          ${className.includes('text-white') ? 'text-white drop-shadow-sm' : 'text-gray-900'}
                          transition-colors duration-300`}>
            Community
          </span>
          <span className={`block font-semibold leading-tight ${config.textSize} -mt-1 
                          ${className.includes('text-white') ? 'text-orange-300 drop-shadow-sm' : 'text-orange-600'}
                          transition-colors duration-300`}>
            ImpactInvest
          </span>
          {size !== 'xs' && size !== 'sm' && size !== 'header' && (
            <p className={`italic mt-1 ${size === 'md' ? 'text-xs' : 'text-sm'} 
                           ${className.includes('text-white') ? 'text-blue-200' : 'text-gray-600'}
                           transition-colors duration-300`}>
              Pour une communauté résiliente
            </p>
          )}
          {size === 'header' && (
            <p className="text-xs italic text-orange-300 leading-none truncate max-w-[120px]">
              Impact social
            </p>
          )}
        </div>
      )}
    </div>
  );

  // Retourner avec ou sans Link selon la configuration
  if (isLink) {
    return (
      <Link to="/" className="no-underline">
        <LogoContent />
      </Link>
    );
  }

  return <LogoContent />;
};

// Composant Logo avec préconfigurations stylisées pour l'utilisation
export const HeaderLogo = () => (
  <CommunityLogo 
    size="header" 
    variant="minimal" 
    showText={true}
    className="text-white hover:text-blue-100 transition-colors duration-300 no-scroll-effects static-logo header-logo-enhanced"
  />
);

// Version ultra-compacte pour header réduit
export const CompactHeaderLogo = () => (
  <CommunityLogo 
    size="xs" 
    variant="minimal" 
    showText={false}
    className="text-white hover:text-blue-100 transition-colors duration-300"
  />
);

export const FooterLogo = () => (
  <CommunityLogo 
    size="sm" 
    variant="minimal" 
    showText={true}
    isLink={true}
    className="hover:bg-blue-800/30 transition-all duration-300"
  />
);

export const LandingLogo = () => (
  <CommunityLogo 
    size="xl" 
    variant="animated" 
    showText={true}
    isLink={false}
    className="mx-auto hover:bg-gradient-to-br hover:from-orange-50/50 hover:to-orange-100/50
               shadow-lg hover:shadow-2xl border border-orange-200/30 hover:border-orange-300/50"
  />
);

export const MobileLogo = () => (
  <CommunityLogo 
    size="sm" 
    variant="default" 
    showText={false}
    isLink={true}
    className="hover:bg-white/10"
  />
);

export const LoadingLogo = () => (
  <CommunityLogo 
    size="lg" 
    variant="gradient" 
    showText={true}
    isLink={false}
    className="animate-pulse hover:animate-none 
               bg-gradient-to-br from-orange-50 to-orange-100 
               shadow-xl border border-orange-200/50"
  />
);

export default CommunityLogo;